import { createContext, useContext, useRef, useCallback, ReactNode } from 'react'

export interface FileMeta {
  name: string;
  file?: File;       // optional for the draft-only phase
  path?: string;     // filled in AFTER upload
  uploadedAt?: any;  // filled in AFTER upload
}

export interface DraftPersona {
  firstName: string;
  lastName: string;
  description: string;
  email: string;
  links: string[];
  files: FileMeta[];
  errors?: Record<string, boolean>; // optional errors for validation
}

interface DraftPersonaContextType {
  draft: DraftPersona;
  addBasicInfo: (info: { firstName?: string; lastName?: string; email?: string; description?: string }) => void;
  addLink: (url: string) => void;
  removeLink: (url: string) => void;
  addFile: (file: File) => void;
  removeFile: (name: string, index: number) => void;
  addErrors: (errors: Record<string, boolean>) => void;
  clearDraft: () => void;
}

const defaultDraft: DraftPersona = {
  firstName: '',
  lastName: '',
  description: '',
  email: '',
  links: [],
  files: [],
  errors: {}
}

const DraftPersonaContext = createContext<DraftPersonaContextType | null>(null)

export const DraftPersonaProvider = ({ children }: { children: ReactNode }) => {
  const draftRef = useRef<DraftPersona>(defaultDraft)

  const addBasicInfo = useCallback((info: { firstName?: string; lastName?: string; email?: string; description?: string }) => {
    draftRef.current = {
      ...draftRef.current,
      ...info,
      errors: {}
    }
  }, [])

  const addLink = useCallback((url: string) => {
    draftRef.current = {
      ...draftRef.current,
      links: [...draftRef.current.links, url],
      errors: {}
    }
  }, [])

  const removeLink = useCallback((url: string) => {
    draftRef.current = {
      ...draftRef.current,
      links: draftRef.current.links.filter(l => l !== url),
      errors: {}
    }
  }, [])

  const addFile = useCallback((file: File) => {
    draftRef.current = {
      ...draftRef.current,
      files: [...draftRef.current.files, { name: file.name, file }],
      errors: {}
    }
  }, [])

  const removeFile = useCallback((name: string, index: number) => {
    draftRef.current = {
      ...draftRef.current,
      files: draftRef.current.files.filter((fm, idx) => idx !== index),
      errors: {}
    }
  }, [])

  const addErrors = useCallback((errors: Record<string, boolean>) => {
    draftRef.current = {
      ...draftRef.current,
      errors
    }
  }, [])

  const clearDraft = useCallback(() => {
    draftRef.current = defaultDraft
  }, [])

  const value = {
    get draft() {
      return draftRef.current
    },
    addBasicInfo,
    addLink,
    removeLink,
    addFile,
    removeFile,
    addErrors,
    clearDraft
  }

  return (
    <DraftPersonaContext.Provider value={value}>
      {children}
    </DraftPersonaContext.Provider>
  )
}

export const useDraftPersona = () => {
  const context = useContext(DraftPersonaContext)
  if (!context) {
    throw new Error('useDraftPersona must be used within a DraftPersonaProvider')
  }
  return context
}